import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import { AuthLayout } from '@/components/AuthLayout';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { ErrorMessage } from '@/components/ErrorMessage';
import { Logo } from '@/components/Logo';
import { AuthFooter } from '@/components/AuthFooter';
import { colors } from '@/styles/colors';
import { ROUTES, EXTERNAL_URLS, ERROR_MESSAGES } from '@/utils/constants';
import { validateEmail } from '@/services/validationService';
import { sendPasswordResetEmail } from '@/api/auth';

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isDesktop = width >= 768;
  const showSSO = process.env.EXPO_PUBLIC_SSO_ENABLED === 'true';

  const handleSubmit = async () => {
    setEmailError(null);
    setError(null);
    setSuccess(false);

    // Validate email
    const emailValidation = validateEmail(email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail({ email });
      setSuccess(true);
    } catch (err: any) {
      setError(ERROR_MESSAGES.PASSWORD_RESET_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.replace(ROUTES.LOGIN);
  };

  const handleSSOClick = () => {
    router.push(ROUTES.SSO);
  };

  return (
    <AuthLayout>
      <View style={styles.formContainer}>
        <Logo />
        
        <View style={styles.formContent}>
          <Text style={styles.heading}>Forgot Password</Text>
          <Text style={[styles.subheading, isDesktop && styles.subheadingDesktop]}>
            Enter your email address and we'll send you a link to reset your password.
          </Text>

          {error && <ErrorMessage message={error} />}
          {success && (
            <View style={styles.successMessage}>
              <Text style={styles.successText}>
                {ERROR_MESSAGES.PASSWORD_RESET_SUCCESS}
              </Text>
            </View>
          )}

          <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email address"
            keyboardType="email-address"
            autoComplete="email"
            maxLength={254}
            error={emailError || undefined}
            onSubmitEditing={handleSubmit}
          />

          <View style={isDesktop && styles.buttonContainer}>
            <Button
              title="Reset Password"
              onPress={handleSubmit}
              loading={loading}
            />
          </View>

          <View style={[styles.forgotEmailContainer, isDesktop && styles.buttonContainer]}>
            <Button
              title="Forgot Email?"
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = EXTERNAL_URLS.SUPPORT_EMAIL;
                }
              }}
              variant="secondary"
            />
          </View>

          <AuthFooter
            variant="forgot-password"
            onBackToLogin={handleBack}
            onSSOClick={handleSSOClick}
            showSSO={showSSO}
          />
        </View>
      </View>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: '100%',
  },
  formContent: {
    width: '100%',
  },
  heading: {
    fontSize: 32,
    lineHeight: 40,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  subheading: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.textSecondary,
    marginBottom: 32,
  },
  subheadingDesktop: {
    marginBottom: 24,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
  forgotEmailContainer: {
    marginTop: 16,
  },
  successMessage: {
    backgroundColor: '#d4edda',
    borderRadius: 8,
    padding: 12,
    paddingHorizontal: 36,
    marginBottom: 16,
  },
  successText: {
    color: '#155724',
    fontSize: 14,
    textAlign: 'center',
  },
});
